// CspConfigContext — per-site Content Security Policy state.
//
// Mirrors DdosConfigContext / BMConfigContext:
//   * one context per subsystem, one hook wrapper
//   * useReducer for the state machine, useCallback for actions
//   * axios v1 user client (auto-scopes tenant, handles refresh)
//
// State surfaces:
//   * cspConfig       — result of GET /config/csp/:site_uid/config
//                       (mode, policy_text, enabled, report_token,
//                       report_rate_cap_per_min)
//   * cspViolations   — result of GET /config/csp/:site_uid/violations
//                       (window_hours, total_count, by_directive,
//                       by_blocked_uri, by_source_format, recent)
//   * errMsg          — surfaced to the notification bar by the page
//                       component
//
// The provider is wrapped around the CSP page in routes.js (see the
// bot / ddos pattern).

import { createContext, useReducer, useCallback } from "react";
import axios from "../../utils/axios/v1/userAxios";

const GET_CSP_CONFIG = "GET_CSP_CONFIG";
const GET_CSP_VIOLATIONS = "GET_CSP_VIOLATIONS";
const SET_ERROR = "SET_ERROR";

const initialState = {
  cspConfig: null,
  cspViolations: null,
  errMsg: null,
};

const CspConfigReducer = (state, action) => {
  switch (action.type) {
    case GET_CSP_CONFIG:
      return { ...state, cspConfig: action.payload.cspConfig };
    case GET_CSP_VIOLATIONS:
      return { ...state, cspViolations: action.payload.cspViolations };
    case SET_ERROR:
      return { ...state, errMsg: action.payload.errMsg };
    default:
      return state;
  }
};

const CspConfigContext = createContext(null);

function CspConfigProvider({ children }) {
  const [state, dispatch] = useReducer(CspConfigReducer, initialState);

  const setErr = useCallback((msg) => {
    dispatch({ type: SET_ERROR, payload: { errMsg: msg } });
  }, []);

  const getCspConfig = useCallback(
    async (siteUid) => {
      dispatch({ type: GET_CSP_CONFIG, payload: { cspConfig: null } });
      try {
        const response = await axios.get(`config/csp/${siteUid}/config`);
        dispatch({
          type: GET_CSP_CONFIG,
          payload: { cspConfig: response.data },
        });
      } catch (err) {
        dispatch({ type: GET_CSP_CONFIG, payload: { cspConfig: null } });
        setErr(err.message);
      }
    },
    [setErr]
  );

  // Partial update. Server accepts any subset of:
  //   mode, policy_text, enabled, report_rate_cap_per_min,
  //   discovery_start_at, discovery_end_at, rotate_report_token
  const updateCspConfig = useCallback(
    async (siteUid, params) => {
      try {
        const response = await axios.patch(
          `config/csp/${siteUid}/config`,
          params
        );
        dispatch({
          type: GET_CSP_CONFIG,
          payload: { cspConfig: response.data },
        });
        return true;
      } catch (err) {
        setErr(err.message);
        await getCspConfig(siteUid);
        return false;
      }
    },
    [setErr, getCspConfig]
  );

  const getCspViolations = useCallback(
    async (siteUid, opts = {}) => {
      const params = {};
      if (opts.windowHours) params.window_hours = opts.windowHours;
      if (opts.recentLimit) params.recent_limit = opts.recentLimit;
      if (opts.directive) params.directive = opts.directive;
      try {
        const response = await axios.get(
          `config/csp/${siteUid}/violations`,
          { params }
        );
        dispatch({
          type: GET_CSP_VIOLATIONS,
          payload: { cspViolations: response.data },
        });
      } catch (err) {
        // Non-fatal: the violations panel degrades to "no data" when
        // the endpoint is unreachable, same posture as DDoS
        // observability.
        dispatch({
          type: GET_CSP_VIOLATIONS,
          payload: { cspViolations: null },
        });
      }
    },
    []
  );

  // Phase 2.2a — call the CH-backed candidate-policy generator.
  // No global state slot; the caller (the DiscoveryActiveBanner
  // dialog) owns the result so the operator can regenerate freely
  // without invalidating anything else on the page. Rejects the
  // promise on failure so the caller can render an error state.
  const generateCandidatePolicy = useCallback(
    async (siteUid, opts = {}) => {
      const params = {};
      if (opts.windowHours) params.window_hours = opts.windowHours;
      if (opts.include_unsafe_inline) params.include_unsafe_inline = "1";
      if (opts.include_unsafe_eval) params.include_unsafe_eval = "1";
      const response = await axios.get(
        `config/csp/${siteUid}/generate-policy`,
        { params }
      );
      return response.data;
    },
    []
  );

  // Phase 2.3a — replay a candidate policy against historical
  // violations and return coverage stats + a top-N still-blocked
  // breakdown. Same posture as generateCandidatePolicy: no global
  // state, caller owns the result.
  const simulatePolicy = useCallback(
    async (siteUid, opts = {}) => {
      const body = { policy_text: opts.policy_text };
      if (opts.windowHours) body.window_hours = opts.windowHours;
      const response = await axios.post(
        `config/csp/${siteUid}/simulate-policy`,
        body
      );
      return response.data;
    },
    []
  );

  // Phase 3.1a — per-site script inventory (PCI DSS 6.4.3 read
  // side). Caller owns the result; the CSV download is a direct
  // navigation via axios' baseURL + `/script-inventory.csv` so we
  // don't have to buffer the file through the JS heap.
  const getScriptInventory = useCallback(
    async (siteUid, opts = {}) => {
      const params = {};
      if (opts.windowHours) params.window_hours = opts.windowHours;
      if (opts.page !== undefined) params.page = opts.page;
      if (opts.limit !== undefined) params.limit = opts.limit;
      if (opts.status && opts.status !== "all") params.status = opts.status;
      if (opts.classification && opts.classification !== "all") params.classification = opts.classification;
      if (opts.q) params.q = opts.q;
      const response = await axios.get(
        `config/csp/${siteUid}/script-inventory`,
        { params }
      );
      return response.data;
    },
    []
  );

  // Phase 3.2a — write side of the PCI DSS 6.4.3 authorization
  // ledger. Accepts a single row `{ url, classification, host?,
  // scheme?, status, justification? }` or bulk `{ items: [...] }`.
  const authorizeScript = useCallback(
    async (siteUid, body) => {
      const response = await axios.post(
        `config/csp/${siteUid}/script-inventory/authorize`,
        body
      );
      return response.data;
    },
    []
  );

  // Phase 3.3a — PCI DSS 11.6.1 change/tamper-detection triggers.
  // recheckIntegrity fetches + hashes every authorized script for
  // the site. acknowledgeDrift rotates the reference for a single
  // urlHash (optionally to an explicit newHash the operator has
  // approved).
  const recheckIntegrity = useCallback(
    async (siteUid) => {
      const response = await axios.post(
        `config/csp/${siteUid}/script-inventory/recheck-integrity`
      );
      return response.data;
    },
    []
  );

  const acknowledgeDrift = useCallback(
    async (siteUid, body) => {
      const response = await axios.post(
        `config/csp/${siteUid}/script-inventory/acknowledge-drift`,
        body
      );
      return response.data;
    },
    []
  );

  // Phase 6.3 — CRUD over per-route CSP overrides. Same caller-owns
  // pattern as intel / simulate. The page component holds the list
  // state so create/delete feel local.
  const listRouteOverrides = useCallback(async (siteUid) => {
    const response = await axios.get(`config/csp/${siteUid}/route-overrides`);
    return response.data;
  }, []);
  const upsertRouteOverride = useCallback(async (siteUid, body) => {
    const response = await axios.post(`config/csp/${siteUid}/route-overrides`, body);
    return response.data;
  }, []);
  const deleteRouteOverride = useCallback(async (siteUid, overrideId) => {
    const response = await axios.delete(
      `config/csp/${siteUid}/route-overrides/${overrideId}`
    );
    return response.data;
  }, []);

  // Cross-tenant reach lookup. Given a list of URLs (usually the
  // caller's own inventory), returns { url: { tenants, reports, ...} }
  // where `tenants` is the count of distinct Layerd customers that
  // have blocked the same URL in the last 30d. Signals to the
  // operator whether an off-inventory hit is unique (targeted /
  // config error) or widespread (noisy third party, safer to allow).
  const getSharedIntel = useCallback(async (siteUid, urls) => {
    const response = await axios.post(
      `config/csp/${siteUid}/shared-intel`,
      { urls }
    );
    return response.data;
  }, []);

  // Phase 5 — CH-backed violation intelligence aggregates for the
  // Violation Intelligence panel in Config.js: time-series + top
  // violator clusters + source-file attribution + campaigns. Zero-
  // state is the backend's job (returns total_count: 0); we fail
  // silent to null so the panel shows its own empty-state copy
  // rather than a global error toast.
  const getViolationIntel = useCallback(async (siteUid, opts = {}) => {
    const params = {};
    if (opts.windowHours) params.window_hours = opts.windowHours;
    try {
      const response = await axios.get(
        `config/csp/${siteUid}/violations/intel`,
        { params }
      );
      return response.data;
    } catch (err) {
      return null;
    }
  }, []);

  // Is the edge collecting anything for this site right now? Returns
  // 60-min report count + last-report timestamp so the operator can
  // catch dead configurations at a glance.
  const getEdgeHealth = useCallback(async (siteUid) => {
    const response = await axios.get(`config/csp/${siteUid}/edge-health`);
    return response.data;
  }, []);

  // Download actions — go through the authenticated axios client so
  // the Bearer token is injected on the wire. A plain `<a href>` click
  // is a browser navigation, which skips the axios interceptor stack
  // and yields a 401 "No bearer token found" from the CP.
  const downloadScriptInventoryCsv = useCallback(async (siteUid, opts = {}) => {
    const params = {};
    if (opts.windowHours) params.window_hours = opts.windowHours;
    const response = await axios.get(
      `config/csp/${siteUid}/script-inventory.csv`,
      { params, responseType: "blob" }
    );
    return response.data;
  }, []);
  const downloadPciEvidenceCsv = useCallback(async (siteUid, opts = {}) => {
    const params = {};
    if (opts.windowHours) params.window_hours = opts.windowHours;
    const response = await axios.get(
      `config/csp/${siteUid}/pci-evidence.csv`,
      { params, responseType: "blob" }
    );
    return response.data;
  }, []);
  // Branded PDF variant of the PCI evidence bundle — server-rendered
  // via PDFKit + Inter + Layerd mark. Scope: authorized (payment-page)
  // scripts only. Same auth path as the CSV.
  const downloadPciEvidencePdf = useCallback(async (siteUid, opts = {}) => {
    const params = {};
    if (opts.windowHours) params.window_hours = opts.windowHours;
    const response = await axios.get(
      `config/csp/${siteUid}/pci-evidence.pdf`,
      { params, responseType: "blob" }
    );
    return response.data;
  }, []);

  return (
    <CspConfigContext.Provider
      value={{
        ...state,
        getCspConfig,
        updateCspConfig,
        getCspViolations,
        generateCandidatePolicy,
        simulatePolicy,
        getScriptInventory,
        authorizeScript,
        recheckIntegrity,
        acknowledgeDrift,
        listRouteOverrides,
        upsertRouteOverride,
        deleteRouteOverride,
        getSharedIntel,
        getViolationIntel,
        getEdgeHealth,
        downloadScriptInventoryCsv,
        downloadPciEvidenceCsv,
        downloadPciEvidencePdf,
        setErr,
      }}
    >
      {children}
    </CspConfigContext.Provider>
  );
}

export { CspConfigContext, CspConfigProvider };
